import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Tags } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { EquipmentForm } from "@/components/equipment/EquipmentForm";
import { ColumnPicker } from "@/components/ColumnPicker";
import {
  EquipmentPagination,
  EquipmentTable,
} from "@/components/equipment/EquipmentTable";
import { EQUIPMENT_LIST_COLUMNS } from "@/components/equipment/equipment-list-columns";
import {
  useEquipment,
  useCreateEquipment,
  type EquipmentInsert,
} from "@/hooks/useEquipment";
import { useEquipmentListColumns } from "@/hooks/useEquipmentListColumns";
import { useEquipmentListParams } from "@/hooks/useEquipmentListParams";

const LATEST_CHANGES_COUNT = 20;

const EquipmentPage = () => {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { search, page, setSearch, setPage } = useEquipmentListParams();
  const isSearching = search.trim().length > 0;

  const { data, isLoading, isFetching } = useEquipment(
    isSearching
      ? { search, page }
      : { sortBy: "updated_at", pageSize: LATEST_CHANGES_COUNT },
  );
  const equipment = data?.items ?? [];

  const createEquipment = useCreateEquipment();
  const { columns, visibleColumns, setColumnVisible, resetColumns } =
    useEquipmentListColumns();

  // The latest changes list always shows when each item was changed
  const latestChangesColumns = useMemo(
    () =>
      visibleColumns.some((c) => c.key === "updated_at")
        ? visibleColumns
        : [
            ...visibleColumns,
            ...EQUIPMENT_LIST_COLUMNS.filter((c) => c.key === "updated_at"),
          ],
    [visibleColumns],
  );

  const handleCreate = (data: EquipmentInsert, tagIds: string[]) => {
    createEquipment.mutate(
      { equipment: data, tagIds },
      {
        onSuccess: (newEquipment) => {
          setIsCreateDialogOpen(false);
          navigate(`/equipment/${newEquipment.id}`);
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Equipment">
        <Button variant="outline" asChild>
          <Link to="/equipment/tags" data-testid="manage-equipment-tags">
            <Tags className="h-4 w-4 mr-2" />
            Manage Tags
          </Link>
        </Button>
        <Button
          offlineEdit
          onClick={() => setIsCreateDialogOpen(true)}
          data-testid="new-equipment-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Equipment
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name, manufacturer, number, short name..."
          />
        </div>
        <ColumnPicker
          columns={columns}
          visibleColumns={visibleColumns}
          onColumnVisibleChange={setColumnVisible}
          onReset={resetColumns}
          testIdPrefix="equipment"
        />
      </div>

      <section className="space-y-3">
        <h2
          className="text-lg font-semibold"
          data-testid="equipment-list-heading"
        >
          {isSearching
            ? `Search results (${data?.totalCount ?? 0})`
            : "Latest changes"}
        </h2>
        <EquipmentTable
          items={equipment}
          columns={isSearching ? visibleColumns : latestChangesColumns}
          isLoading={isLoading}
          isFetching={isFetching}
          emptyMessage={
            isSearching ? "No equipment matches your search" : undefined
          }
        />
        {isSearching && (
          <EquipmentPagination
            page={page}
            totalPages={data?.totalPages ?? 1}
            totalCount={data?.totalCount ?? 0}
            onPageChange={setPage}
          />
        )}
      </section>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Equipment</DialogTitle>
          </DialogHeader>
          <EquipmentForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createEquipment.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentPage;
